import {groupBy} from 'lodash';
import React from 'react';
import {useQuery} from 'react-query';

import Gauges from 'src/components/Gauges';
import GeoViz from 'src/components/GeoViz';
import FlowSection from 'src/components/h5/FlowSection';
import IncludeFlowContributingAreasSwitch from 'src/components/IncludeContributingBasinsSwitch';
import LoadingSpinner from 'src/components/LoadingSpinner';
import Seasonal from 'src/components/Seasonal';
import SeasonalVolume from 'src/components/SeasonalVolume';
import ShortTerm from 'src/components/ShortTerm';
import Surface from 'src/components/Surface';
import SWEAnalysis from 'src/components/SWEAnalysis';
import {MouseDateProvider} from 'src/components/Tooltip';
import {useTranslations} from 'src/components/TranslationsContext';
import WithWidth from 'src/components/WithWidth';
import {StagingForecastToggleProvider} from 'src/contexts/StagingToggleContext';
import {UnitSystemProvider} from 'src/contexts/UnitSystemContext';
import {useH5Preview} from 'src/hooks/h5/useH5Preview';
import {LoggedInAuth} from 'src/hooks/useAuth';
import {MaybeApiFlowContributingAreas} from 'src/hooks/useFlowContributingAreas';
import {fetchAvailableAnalyses} from 'src/lib/api/analysis';
import {ApiUserProfile} from 'src/lib/api/auth';
import {Project, Site} from 'src/lib/api/project';
import {DashboardSection} from 'src/lib/api/series';
import {Dims} from 'src/lib/commonTypes';
import {addDays, getWaterYearDateRange, getYearDateRange} from 'src/lib/dates';
import {h5DashboardSectionsFor, legacySeasonalSeriesTypeFor} from 'src/lib/h5/sections';
import {SITE_MAP_HEIGHT_COLLAPSED, SITE_MAP_HEIGHT_EXPANDED} from 'src/lib/map';
import {
  LOCAL_SITE_PATTERN,
  canUseBasinAveraging,
  createSiteGroupId,
  hasDailyLTAFlow,
  hasFullReleaseSeasonalModel,
  hasGaugeElevation,
  hasGaugeFlow,
  hasRollingSeasonalFlow,
  hasShortTermFlow,
  hasShortTermWeather,
  hasSurface,
} from 'src/lib/siteUtils';
import * as tw from 'src/lib/tailwindClasses';

type SurfaceDateRangeOptions =
  | 'current-water-year'
  | 'current-year'
  | 'last-12-months'
  | 'all-years';

type Props = {
  auth: LoggedInAuth;
  project: Project;
  flowContributingAreas: MaybeApiFlowContributingAreas;
  currentUser: ApiUserProfile | null;
  selectedSiteId: Site['id'] | null;
  setSelectedSiteId: (siteId: Site['id']) => void;
};

export function ProjectSiteSection({
  auth,
  project,
  flowContributingAreas,
  currentUser,
  selectedSiteId,
  setSelectedSiteId,
}: Props) {
  const {t} = useTranslations();
  const groupedSites = groupBy(project.sites, (s) => createSiteGroupId(s.id));
  const selectedSite = project.sites?.find((site) => site.id === selectedSiteId) || null;

  const [_includeFlowContributingAreas, setIncludeFlowContributingAreas] = React.useState<boolean>(
    currentUser?.settings?.defaultWeatherDisplay === 'basinAverage'
  );
  // Force to false on sites/projects that don't support basin averaging.
  // Computed synchronously to avoid race conditions with updating the state.
  const includeFlowContributingAreas = canUseBasinAveraging(
    selectedSite,
    project.basinAveragingEnabled,
    flowContributingAreas
  )
    ? _includeFlowContributingAreas
    : false;

  const [selectedSurfaceDateRangeOption, setSelectedSurfaceDateRangeOption] =
    React.useState<SurfaceDateRangeOptions>('last-12-months');
  const [surfaceDateRange, setSurfaceDateRange] = React.useState<[Date, Date] | null>();
  const [mapExpanded, setMapExpanded] = React.useState<boolean>(false);
  const mapHeight = mapExpanded ? SITE_MAP_HEIGHT_EXPANDED : SITE_MAP_HEIGHT_COLLAPSED;

  React.useEffect(() => {
    if (selectedSurfaceDateRangeOption === 'current-water-year') {
      setSurfaceDateRange(getWaterYearDateRange(new Date()));
    } else if (selectedSurfaceDateRangeOption === 'current-year') {
      setSurfaceDateRange(getYearDateRange(new Date()));
    } else if (selectedSurfaceDateRangeOption === 'last-12-months') {
      setSurfaceDateRange([addDays(new Date(), -365), new Date()]);
    } else if (selectedSurfaceDateRangeOption === 'all-years') {
      setSurfaceDateRange([new Date(2000, 0, 0), new Date()]);
    }
  }, [selectedSurfaceDateRangeOption]);

  React.useEffect(() => {
    if (project && project.sites && !selectedSiteId) {
      const firstGroup = groupedSites[createSiteGroupId(project.sites[0].id)];
      if (firstGroup.length > 1) {
        setSelectedSiteId(
          (firstGroup.find((s) => !LOCAL_SITE_PATTERN.test(s.id)) || firstGroup[0]).id
        );
      } else {
        setSelectedSiteId(firstGroup[0].id);
      }
    }
  }, [project, selectedSiteId, setSelectedSiteId, groupedSites]);

  const availableAnalysesQuery = useQuery(
    ['availableAnalyses', selectedSiteId],
    () => fetchAvailableAnalyses(selectedSite!.id, selectedSite!.projectId),
    {enabled: !!selectedSite, staleTime: Infinity}
  );

  const h5Preview = useH5Preview();

  if (!selectedSiteId || !selectedSite) {
    return (
      <div className="flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Which sections are drawn by their H5 component rather than the legacy one. Each component
  // reads its own source, so this choice is the only place the two meet.
  const h5DashboardSections = h5DashboardSectionsFor(project, selectedSite, h5Preview.enabled);
  const legacySeasonalSeriesType = legacySeasonalSeriesTypeFor(selectedSite, h5DashboardSections);

  return (
    <UnitSystemProvider unitSystem={project.unitSystem}>
      <StagingForecastToggleProvider>
        <MouseDateProvider>
          <div className="relative flex flex-wrap shadow rounded border border-gray-100">
            <div className="w-full" style={{height: mapHeight}}>
              <WithWidth>
                {(width) => {
                  const dims: Dims = [width, mapHeight];
                  return (
                    <GeoViz
                      firebaseToken={auth.firebaseToken}
                      dims={dims}
                      project={project}
                      flowContributingAreas={flowContributingAreas}
                      selectedSite={selectedSite!}
                      setSelectedSiteId={setSelectedSiteId}
                      expanded={mapExpanded}
                      setExpanded={setMapExpanded}
                    />
                  );
                }}
              </WithWidth>
            </div>
          </div>

          {(hasGaugeFlow(selectedSite) || hasGaugeElevation(selectedSite)) && (
            <Gauges site={selectedSite} />
          )}

          {/* SHORT-TERM FORECAST SECTION */}
          {h5DashboardSections.has(DashboardSection.ShortTerm) ? (
            // H5 sections are keyed by site so no section state (aggregation, model choice,
            // forecast selection) carries over to another site.
            <FlowSection
              key={`short-term:${selectedSite.id}`}
              section={DashboardSection.ShortTerm}
              project={project}
              site={selectedSite}
              currentUser={currentUser}
            />
          ) : (
            (hasShortTermFlow(selectedSite) || hasShortTermWeather(selectedSite)) && (
              <ShortTerm
                site={selectedSite}
                defaultDateRange={project.defaultDateRange}
                includeFlowContributingAreas={includeFlowContributingAreas}
                setIncludeFlowContributingAreas={setIncludeFlowContributingAreas}
                project={project}
                flowContributingAreas={flowContributingAreas}
                currentUserId={currentUser?.id}
              />
            )
          )}

          {h5DashboardSections.has(DashboardSection.Seasonal) && (
            // Seasonal and annual have independent date ranges. When H5 seasonal replaces the
            // legacy seasonal section it keeps that section's date param, so existing links keep
            // their range; when the two coexist each needs its own.
            <FlowSection
              key={`seasonal:${selectedSite.id}`}
              section={DashboardSection.Seasonal}
              dateRangeParam={legacySeasonalSeriesType ? 'h5SeasonalDateRange' : undefined}
              project={project}
              site={selectedSite}
              currentUser={currentUser}
            />
          )}
          {h5DashboardSections.has(DashboardSection.Annual) && (
            <FlowSection
              key={`annual:${selectedSite.id}`}
              section={DashboardSection.Annual}
              project={project}
              site={selectedSite}
              currentUser={currentUser}
            />
          )}
          {legacySeasonalSeriesType && (
            <Seasonal
              site={selectedSite}
              seriesType={legacySeasonalSeriesType}
              project={project}
              currentUser={currentUser}
              includeFlowContributingAreas={includeFlowContributingAreas}
              setIncludeFlowContributingAreas={setIncludeFlowContributingAreas}
            />
          )}
          {/* Seasonal Volume reads legacy seasonal forecasts, so it shows whichever component draws the seasonal sections */}
          {/* For Seasonal Volume we also need to make sure that it has historical observations to make the LTA bar chart */}
          {hasRollingSeasonalFlow(selectedSite) &&
            hasDailyLTAFlow(selectedSite) &&
            hasFullReleaseSeasonalModel(selectedSite) && (
              <SeasonalVolume project={project} site={selectedSite} currentUser={currentUser} />
            )}
          {/* SURFACE SECTION*/}
          {hasSurface(selectedSite) && surfaceDateRange && (
            <div className={tw.section}>
              <Surface
                site={selectedSite}
                dateRange={surfaceDateRange}
                includeFlowContributingAreas={includeFlowContributingAreas}
                renderOptions={() => (
                  <>
                    <IncludeFlowContributingAreasSwitch
                      site={selectedSite}
                      basinAveragingEnabled={project.basinAveragingEnabled}
                      includeFlowContributingAreas={includeFlowContributingAreas}
                      setIncludeFlowContributingAreas={setIncludeFlowContributingAreas}
                    />
                    <select
                      className={tw.select}
                      value={selectedSurfaceDateRangeOption}
                      onChange={(e) => {
                        setSelectedSurfaceDateRangeOption(
                          e.target.value as SurfaceDateRangeOptions
                        );
                      }}
                    >
                      <option value="last-12-months">{t('last12Months')}</option>
                      <option value="current-water-year">{t('currentWaterYear')}</option>
                      <option value="current-year">{t('currentYear')}</option>
                      <option value="all-years">{t('allYears')}</option>
                    </select>
                  </>
                )}
              />
            </div>
          )}

          {/* SWE ANALYSIS SECTION */}
          {availableAnalysesQuery.data?.sweAnalysis && (
            <div className={tw.section}>
              <h2 className={tw.sectionTitle}>{t('sweAnalysisTitle')}</h2>
              <div className={tw.sectionBody}>
                <SWEAnalysis
                  siteId={selectedSite.id}
                  projectId={selectedSite.projectId}
                  siteName={selectedSite.name}
                />
              </div>
            </div>
          )}
        </MouseDateProvider>
      </StagingForecastToggleProvider>
    </UnitSystemProvider>
  );
}
